export const stripDiacritics = (s: string): string =>
  (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function fmtYear(y: number | null | undefined): string {
  if (y === undefined || y === null || Number.isNaN(y)) return "";
  if (y < 0) return `${-y} BCE`;
  if (y < 1000) return `${y} CE`;
  return `${y}`;
}

export function fmtDateRange(start?: number | null, end?: number | null): string {
  if (start == null && end == null) return "";
  if (start == null) return `to ${fmtYear(end)}`;
  if (end == null) return `from ${fmtYear(start)}`;
  if (start === end) return fmtYear(start);
  return `${fmtYear(start)} – ${fmtYear(end)}`;
}

export function fmtKm(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

export const humanize = (s: string): string => s.replace(/_/g, " ");

export function tokenize(q: string): string[] {
  return stripDiacritics(q).split(/\s+/).filter(Boolean);
}

export function debounce<T extends (...args: never[]) => void>(fn: T, ms: number): T {
  let t: ReturnType<typeof setTimeout> | undefined;
  return ((...args: Parameters<T>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  }) as T;
}

export function escapeHtml(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
}

export function pluralize(n: number, one: string, many = one + "s"): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * A smaller Wikimedia Commons rendition of a stored photo URL, for list thumbnails.
 * Handles both original (`/commons/a/ab/File.jpg`) and thumb (`/commons/thumb/a/ab/File.jpg/1280px-File.jpg`)
 * forms; anything else is returned unchanged. Wikimedia only renders an allow-list of
 * widths (others answer 400), so the requested width snaps up to the nearest allowed.
 */
const COMMONS_WIDTHS = [250, 330, 500, 960, 1280];
export function commonsThumb(url: string, width: number): string {
  try {
    const u = new URL(url);
    if (u.hostname !== "upload.wikimedia.org") return url;
    const m = u.pathname.match(/^\/wikipedia\/commons\/(thumb\/)?([0-9a-f])\/([0-9a-f]{2})\/([^/]+)(?:\/\d+px-[^/]+)?$/);
    if (!m) return url;
    const [, , a, ab, file] = m;
    if (/\.svg$/i.test(file)) return url;
    const w = COMMONS_WIDTHS.find((x) => x >= width) ?? COMMONS_WIDTHS[COMMONS_WIDTHS.length - 1];
    return `https://upload.wikimedia.org/wikipedia/commons/thumb/${a}/${ab}/${file}/${w}px-${file}`;
  } catch {
    return url;
  }
}
